import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './LoadCells.css';
import { useProducts, type LoadCellsSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const LoadCells: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products, loading, error } = useProducts<LoadCellsSpecs>('load-cells', editMode);

  const includedComponents = [
    '3-4 celdas de carga Weigh Bar',
    'Hardware de montaje',
    'Soporte estructural',
    'Eslabón de conexión',
    'Eslabón de seguridad',
    'Pieza en "U" roscada para fijación',
    'Caja de conexiones sellada',
    '7.6 metros de cable blindado'
  ];

  const applications = [
    {
      title: 'Silos',
      description: 'Conversión de silos de almacenamiento en básculas para control de inventario',
    },
    {
      title: 'Tanques',
      description: 'Transformación de tanques industriales en sistemas de pesaje continuo',
    },
    {
      title: 'Tolvas',
      description: 'Integración en tolvas para pesaje automático de materiales',
    },
    {
      title: 'Transportadores',
      description: 'Instalación en bandas transportadoras para pesaje dinámico',
    },
    {
      title: 'Reactores',
      description: 'Aplicación en reactores químicos para control de procesos',
    },
    {
      title: 'Mezcladores',
      description: 'Integración en equipos de mezclado para dosificación precisa',
    }
  ];

  const features = [
    {
      title: 'Sistema de Flotación Libre',
      description: 'Absorbe deformaciones térmicas y mecánicas automáticamente',
      benefit: 'Mayor precisión y vida útil'
    },
    {
      title: 'Construcción Robusta',
      description: 'Disponible en acero al carbón o acero inoxidable',
      benefit: 'Resistencia en ambientes industriales'
    },
    {
      title: 'Protección Ambiental',
      description: 'Resistente a polvo y humedad para uso industrial',
      benefit: 'Operación confiable en cualquier ambiente'
    },
    {
      title: 'Amplio Rango de Capacidad',
      description: 'Desde 170 kg hasta 90,000 kg de capacidad',
      benefit: 'Solución para cualquier aplicación'
    },
    {
      title: 'Instalación Completa',
      description: 'Incluye todo el hardware y componentes necesarios',
      benefit: 'Fácil implementación'
    },
    {
      title: 'Tecnología Probada',
      description: 'Marca Avery Weigh-Tronix con reputación mundial',
      benefit: 'Confiabilidad garantizada'
    }
  ];

  return (
    <div className="lc-page">
      {error && <div className="lc-status-bar lc-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="lc-hero">
        <div className="container lc-hero-content">
          <nav className="lc-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="lc-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="lc-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Celdas de Carga</span>
          </nav>
          <h1 className="lc-hero-title">Celdas de Carga</h1>
          <p className="lc-hero-subtitle">
            Convierte silos, tanques, tolvas y transportadores en básculas con tecnología Avery Weigh-Tronix
          </p>
          {authType === 'internal' && (
            <button
              className={`lc-btn-edit-mode${editMode ? ' lc-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="lc-hero-image" aria-hidden="true">
          <img src="/images/weigh-bars.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="lc-overview">
        <div className="container">
          <div className="lc-overview-grid">
            <div className="lc-overview-text">
              <span className="lc-label">Descripción general</span>
              <h2>Pesaje preciso en cualquier estructura</h2>
              <p>
                Las celdas de carga Weigh Bar de Avery Weigh-Tronix están diseñadas para convertir
                silos, tanques, tolvas y bandas transportadoras en básculas precisas. Con un sistema
                de suspensión de flotación libre, absorben las deformaciones térmicas y mecánicas,
                proporcionando mediciones exactas y duraderas.
              </p>
              
            </div>
            <div className="lc-overview-image">
             <ul className="lc-feature-list" aria-label="Características principales">
                <li className="lc-feature-item">
                  <svg className="lc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Capacidades desde 170 kg hasta 90,000 kg
                </li>
                <li className="lc-feature-item">
                  <svg className="lc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Construcción en acero al carbón o acero inoxidable
                </li>
                <li className="lc-feature-item">
                  <svg className="lc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Sistema de suspensión de flotación libre
                </li>
                <li className="lc-feature-item">
                  <svg className="lc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Resistente a polvo y humedad
                </li>
                <li className="lc-feature-item">
                  <svg className="lc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Kit completo con hardware de instalación
                </li>
                <li className="lc-feature-item">
                  <svg className="lc-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Marca Avery Weigh-Tronix de confianza mundial
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="lc-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/load-cells')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : products.map((product, index) => (
                  <ProductCard
                    key={index}
                    reviewed={product.reviewed}
                    endOfLife={product.endOfLife}
                    productId={product.productId}
                    name={product.name}
                    badge={product.specs?.model ?? undefined}
                    description={product.specs?.description ?? undefined}
                    image={product.image}
                    brand={product.specs?.brand ?? undefined}
                    specRows={[
                      ...(product.specs?.capacityRange ? [{ label: 'Capacidad',   value: product.specs.capacityRange }] : []),
                      ...(product.specs?.suspension    ? [{ label: 'Suspensión',  value: product.specs.suspension    }] : []),
                      ...(product.specs?.protection    ? [{ label: 'Protección',  value: product.specs.protection    }] : []),
                    ]}
                    onRevisar={editMode && product.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                    editMode={editMode}
                    sections={[
                      ...(product.specs?.features?.length           ? [{ icon: '⚙️', label: 'Características',  items: (product.specs.features           ?? []).filter((x): x is string => x != null) }] : []),
                      ...(product.specs?.includedComponents?.length  ? [{ icon: '📋', label: 'Kit incluye',      items: (product.specs.includedComponents  ?? []).filter((x): x is string => x != null) }] : []),
                      ...(product.specs?.materials?.length           ? [{ icon: '🔩', label: 'Materiales',       items: (product.specs.materials           ?? []).filter((x): x is string => x != null) }] : []),
                      ...(product.specs?.applications?.length        ? [{ icon: '🏭', label: 'Aplicaciones',     items: (product.specs.applications        ?? []).filter((x): x is string => x != null) }] : []),
                    ]}
                  />
                ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Included Components ───────────────────────────────── */}
      <section className="lc-components">
        <div className="container">
          <span className="lc-label">Kit de instalación</span>
          <h2 className="lc-section-title">Componentes incluidos</h2>
          <p className="lc-section-intro">
            Cada kit de celdas de carga Weigh Bar incluye todos los componentes necesarios
            para una instalación completa y funcional.
          </p>
          <div className="lc-components-grid">
            {includedComponents.map((component, index) => (
              <div key={index} className="lc-component-card">
                <h3>{component}</h3>
              </div>
            ))}
          </div>
          <div className="lc-important-note">
            <span className="lc-note-label">Importante</span>
            <p>
              No incluye indicador de peso. El indicador debe adquirirse por separado
              según los requerimientos específicos de la aplicación.
            </p>
          </div>
        </div>
      </section>

      {/* ── Features & Benefits ───────────────────────────────── */}
      <section className="lc-features">
        <div className="container">
          <div className="lc-features-inner">
            <div className="lc-features-header">
              <span className="lc-label">Tecnología</span>
              <h2 className="lc-section-title">Características y beneficios</h2>
              <p>
                El diseño Weigh Bar combina ingeniería probada con facilidad de instalación
                para transformar su infraestructura existente en un sistema de pesaje de alta precisión.
              </p>
            </div>
            <div className="lc-features-grid">
              {features.map((feature, index) => (
                <div key={index} className="lc-feature-card">
                  <h3>{feature.title}</h3>
                  <p className="lc-feature-description">{feature.description}</p>
                  <p className="lc-feature-benefit">{feature.benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="lc-applications">
        <div className="container">
          <span className="lc-label lc-label--light">Industrias</span>
          <h2 className="lc-section-title lc-section-title--light">Aplicaciones</h2>
          <div className="lc-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="lc-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Installation Process ──────────────────────────────── */}
      <section className="lc-installation">
        <div className="container">
          <span className="lc-label">Implementación</span>
          <h2 className="lc-section-title">Proceso de instalación</h2>
          <p className="lc-section-intro">
            La instalación de las celdas de carga Weigh Bar es un proceso sistemático
            que transforma su equipo existente en una báscula precisa y confiable.
          </p>
          <div className="lc-steps-grid">
            <div className="lc-step-card">
              <span className="lc-step-number">01</span>
              <h3>Evaluación del equipo</h3>
              <p>Análisis de la estructura existente y determinación de puntos de instalación óptimos</p>
            </div>
            <div className="lc-step-card">
              <span className="lc-step-number">02</span>
              <h3>Preparación estructural</h3>
              <p>Modificaciones menores necesarias para el montaje de las celdas de carga</p>
            </div>
            <div className="lc-step-card">
              <span className="lc-step-number">03</span>
              <h3>Instalación de celdas</h3>
              <p>Montaje de 3-4 celdas Weigh Bar con hardware de soporte incluido</p>
            </div>
            <div className="lc-step-card">
              <span className="lc-step-number">04</span>
              <h3>Conexión eléctrica</h3>
              <p>Instalación de caja de conexiones y cableado al indicador de peso</p>
            </div>
            <div className="lc-step-card">
              <span className="lc-step-number">05</span>
              <h3>Calibración</h3>
              <p>Calibración precisa del sistema para garantizar mediciones exactas</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Advantages ───────────────────────────────────────── */}
      <section className="lc-advantages">
        <div className="container">
          <span className="lc-label">Por qué elegirnos</span>
          <h2 className="lc-section-title">Ventajas de las celdas de carga Weigh Bar</h2>
          <div className="lc-advantages-grid">
            <div className="lc-advantage-card">
              <h3>Tecnología probada</h3>
              <p>Avery Weigh-Tronix es líder mundial en tecnología de pesaje industrial</p>
            </div>
            <div className="lc-advantage-card">
              <h3>Fácil retrofit</h3>
              <p>Convierte equipos existentes sin necesidad de reemplazo completo</p>
            </div>
            <div className="lc-advantage-card">
              <h3>Alta precisión</h3>
              <p>Sistema de flotación libre para mediciones exactas y repetibles</p>
            </div>
            <div className="lc-advantage-card">
              <h3>Durabilidad</h3>
              <p>Construcción robusta para resistir ambientes industriales exigentes</p>
            </div>
            <div className="lc-advantage-card">
              <h3>Versatilidad</h3>
              <p>Amplio rango de capacidades para diversas aplicaciones</p>
            </div>
            <div className="lc-advantage-card">
              <h3>Costo-efectivo</h3>
              <p>Solución económica comparada con reemplazo de equipos completos</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LoadCells;
